import { MakeLogicType, actions, connect, events, kea, listeners, path, props, reducers, selectors } from 'kea'
import { combineUrl, router } from 'kea-router'
import { subscriptions } from 'kea-subscriptions'
import posthog from 'posthog-js'
import React from 'react'

import {
    IconChat,
    IconCursorClick,
    IconDashboard,
    IconDatabase,
    IconDecisionTree,
    IconGraph,
    IconHome,
    IconLive,
    IconLlmAnalytics,
    IconMessage,
    IconNotebook,
    IconPeople,
    IconPieChart,
    IconPiggyBank,
    IconPlug,
    IconPlusSmall,
    IconRewindPlay,
    IconRocket,
    IconServer,
    IconSpotlight,
    IconTestTube,
    IconToggle,
    IconWarning,
} from '@posthog/icons'
import { Spinner, lemonToast } from '@posthog/lemon-ui'

import { FEATURE_FLAGS } from 'lib/constants'
import { LemonMenuOverlay } from 'lib/lemon-ui/LemonMenu/LemonMenu'
import { featureFlagLogic } from 'lib/logic/featureFlagLogic'
import { getAppContext } from 'lib/utils/getAppContext'
import { isNotNil } from 'lib/utils/guards'
import { editorSceneLogic } from 'scenes/data-warehouse/editor/editorSceneLogic'
import { onboardingVariantChrome, resolveOnboardingFlowVariant } from 'scenes/onboarding/onboardingVariants'
import { organizationLogic } from 'scenes/organizationLogic'
import { sceneLogic } from 'scenes/sceneLogic'
import { Scene } from 'scenes/sceneTypes'
import { sessionRecordingSavedFiltersLogic } from 'scenes/session-recordings/filters/sessionRecordingSavedFiltersLogic'
import { urls } from 'scenes/urls'

import { dashboardsModel } from '~/models/dashboardsModel'
import { groupsModel } from '~/models/groupsModel'
import { AccessControlLevel, AccessControlResourceType, ReplayTabs } from '~/types'

import type { GroupsAccessStatus } from '../../lib/introductions/groupsAccessLogic'
import type { SceneConfig, SceneExport, SceneParams, SceneProps } from '../../scenes/sceneTypes'
import type { GroupType, GroupTypeIndex, SavedSessionRecordingPlaylistsResult } from '../../types'
import { navigationLogic } from '../navigation/navigationLogic'
import { BasicListItem, ExtendedListItem, NavbarItem, SidebarNavbarItem } from './types'

/** Multi-segment item keys are joined using this separator for easy comparisons. */
export const ITEM_KEY_PART_SEPARATOR = '::'

export type Navigation3000Mode = 'none' | 'minimal' | 'zen' | 'full'

export type ZenModeTrigger = 'shortcut' | 'account_menu' | 'help_menu' | 'exit_button' | 'url'

const MINIMUM_SIDEBAR_WIDTH_PX: number = 192
const DEFAULT_SIDEBAR_WIDTH_PX: number = 288
const MAXIMUM_SIDEBAR_WIDTH_PX: number = 1024
const MAXIMUM_SIDEBAR_WIDTH_PERCENTAGE: number = 50

// Generated by kea-typegen. Update if you're an agent, ignore if you're human.
export interface navigation3000LogicValues {
    groupTypes: Map<GroupTypeIndex, GroupType> // groupsModel
    groupsAccessStatus: GroupsAccessStatus // groupsModel
    mobileLayout: boolean // navigationLogic
    isCurrentOrganizationUnavailable: boolean // organizationLogic
    activeSceneId: string | null // sceneLogic
    sceneConfig: SceneConfig | null // sceneLogic
    savedFilters: SavedSessionRecordingPlaylistsResult // sessionRecordingSavedFiltersLogic
    savedFiltersLoading: boolean // sessionRecordingSavedFiltersLogic
    accordionCollapseMapping: Record<string, boolean>
    activeNavbarItem: SidebarNavbarItem | null
    activeNavbarItemId: string | null
    activeNavbarItemIdRaw: string
    internalSearchTerm: string
    isListItemVisible: (key: string) => boolean
    isNavCollapsed: boolean
    isNavCollapsedDesktop: boolean
    isNavShown: boolean
    isNavShownMobile: boolean
    isResizeInProgress: boolean
    isSearchShown: boolean
    isSidebarKeyboardShortcutAcknowledged: boolean
    isSidebarShown: boolean
    lastFocusedItemIndex: number
    listItemAccordionCollapseMapping: Record<string, boolean>
    mode: Navigation3000Mode
    navbarItemIdMapping: Record<string, NavbarItem>
    navbarItems: NavbarItem[][]
    newItemCategory: string | null
    newItemInlineCategory: string | null
    normalizedActiveListItemKey: number | string | string[] | null
    savingNewItem: boolean
    searchTerm: string
    sidebarContentsFlattened: BasicListItem[] | ExtendedListItem[]
    sidebarOverslide: number
    sidebarOverslideDirection: 'max' | 'min' | null
    sidebarWidth: number
    zenMode: boolean
    zenModeFromUrl: boolean
}

// Generated by kea-typegen. Update if you're an agent, ignore if you're human.
export interface navigation3000LogicActions {
    setScene: (
        sceneId: string,
        sceneKey: string | undefined,
        params: SceneParams,
        scrollToTop?: boolean | undefined,
        exportedScene?: SceneExport<SceneProps> | undefined
    ) => {
        exportedScene: SceneExport<SceneProps> | undefined
        params: SceneParams
        sceneId: string
        sceneKey: string | undefined
        scrollToTop: boolean
    } // sceneLogic
    acknowledgeSidebarKeyboardShortcut: () => {
        value: true
    }
    beginResize: () => {
        value: true
    }
    cancelNewItem: () => {
        value: true
    }
    endResize: () => {
        value: true
    }
    focusNextItem: () => {
        value: true
    }
    focusPreviousItem: () => {
        value: true
    }
    hideNavOnMobile: () => {
        value: true
    }
    hideSidebar: () => {
        value: true
    }
    initiateNewItemInCategory: (category: string) => {
        category: string
    }
    initiateNewItemInlineInCategory: (category: string) => {
        category: string
    }
    saveNewItem: (itemName: string) => {
        itemName: string
    }
    saveNewItemComplete: () => {
        value: true
    }
    setIsSearchShown: (isSearchShown: boolean) => {
        isSearchShown: boolean
    }
    setLastFocusedItemByKey: (key: number | string) => {
        key: number | string
    }
    setLastFocusedItemIndex: (index: number) => {
        index: number
    }
    setSearchTerm: (searchTerm: string) => {
        searchTerm: string
    }
    setSidebarOverslide: (overslide: number) => {
        overslide: number
    }
    setSidebarWidth: (width: number) => {
        width: number
    }
    setZenMode: (
        zenMode: boolean,
        trigger: ZenModeTrigger
    ) => {
        trigger: ZenModeTrigger
        zenMode: boolean
    }
    showNavOnMobile: () => {
        value: true
    }
    showSidebar: (newNavbarItemId?: string) => {
        newNavbarItemId: string | undefined
    }
    syncSidebarWidthWithMouseMove: (delta: number) => {
        delta: number
    }
    syncSidebarWidthWithViewport: () => {
        value: true
    }
    toggleAccordion: (key: string) => {
        key: string
    }
    toggleListItemAccordion: (key: string) => {
        key: string
    }
    toggleNavCollapsed: (override?: boolean) => {
        override: boolean | undefined
    }
    toggleSidebar: () => {
        value: true
    }
    toggleZenMode: (trigger: ZenModeTrigger) => {
        trigger: ZenModeTrigger
    }
}

// Generated by kea-typegen. Update if you're an agent, ignore if you're human.
export interface navigation3000LogicProps {
    inputElement?: HTMLInputElement | null
}

// Generated by kea-typegen. Update if you're an agent, ignore if you're human.
export interface navigation3000LogicMeta {
    __keaTypeGenInternalSelectorTypes: {
        zenModeFromUrl: (searchParams: Record<string, any>) => boolean
        mode: (
            sceneConfig: SceneConfig | null,
            isCurrentOrganizationUnavailable: boolean,
            zenMode: boolean,
            activeSceneId: string | null,
            featureFlags: import('lib/logic/featureFlagLogic').FeatureFlagsSet
        ) => Navigation3000Mode
        isNavShown: (isNavShownMobile: boolean, mobileLayout: boolean) => boolean
        isNavCollapsed: (isNavCollapsedDesktop: boolean, mobileLayout: boolean) => boolean
        navbarItems: (
            featureFlags: import('lib/logic/featureFlagLogic').FeatureFlagsSet,
            dashboardsLoading: boolean,
            pinnedDashboards: (
                | import('~/types').DashboardBasicType
                | import('~/types').DashboardType<
                      import('~/types').QueryBasedInsightModel<import('../../queries/schema').Node<Record<string, any>>>
                  >
            )[],
            savedFilters: SavedSessionRecordingPlaylistsResult,
            savedFiltersLoading: boolean
        ) => NavbarItem[][]
        navbarItemIdMapping: (navbarItems: NavbarItem[][]) => Record<string, NavbarItem>
        sidebarOverslideDirection: (sidebarOverslide: number) => 'max' | 'min' | null
        activeNavbarItem: (
            activeNavbarItemId: string | null,
            navbarItemIdMapping: Record<string, NavbarItem>
        ) => SidebarNavbarItem | null
        searchTerm: (internalSearchTerm: string, isSearchShown: boolean) => string
        sidebarContentsFlattened: (
            arg: import('./types').SidebarCategory[] | null
        ) => BasicListItem[] | ExtendedListItem[]
        normalizedActiveListItemKey: (
            arg: number | string | [string, number | string] | null
        ) => number | string | string[] | null
        isListItemVisible: (listItemAccordionCollapseMapping: Record<string, boolean>) => (key: string) => boolean
        activeNavbarItemId: (activeNavbarItemIdRaw: string) => string | null
        newItemCategory: (
            arg: import('./types').SidebarCategory[] | null,
            newItemInlineCategory: string | null,
            location: {
                hash: string
                pathname: string
                search: string
            }
        ) => string | null
    }
}

export type navigation3000LogicType = MakeLogicType<
    navigation3000LogicValues,
    navigation3000LogicActions,
    navigation3000LogicProps,
    navigation3000LogicMeta
>

export const navigation3000Logic = kea<navigation3000LogicType>([
    path(['layout', 'navigation-3000', 'navigationLogic']),
    props({} as { inputElement?: HTMLInputElement | null }),
    connect(() => ({
        values: [
            groupsModel,
            ['groupTypes', 'groupsAccessStatus'],
            sceneLogic,
            ['sceneConfig', 'activeSceneId'],
            navigationLogic,
            ['mobileLayout'],
            sessionRecordingSavedFiltersLogic,
            ['savedFilters', 'savedFiltersLoading'],
            organizationLogic,
            ['isCurrentOrganizationUnavailable'],
        ],
        actions: [sceneLogic, ['setScene']],
    })),
    actions({
        hideSidebar: true,
        showSidebar: (newNavbarItemId?: string) => ({ newNavbarItemId }),
        toggleNavCollapsed: (override?: boolean) => ({ override }),
        showNavOnMobile: true,
        hideNavOnMobile: true,
        toggleSidebar: true,
        setSidebarWidth: (width: number) => ({ width }),
        setSidebarOverslide: (overslide: number) => ({ overslide }),
        syncSidebarWidthWithMouseMove: (delta: number) => ({ delta }),
        syncSidebarWidthWithViewport: true,
        beginResize: true,
        endResize: true,
        acknowledgeSidebarKeyboardShortcut: true,
        setIsSearchShown: (isSearchShown: boolean) => ({ isSearchShown }),
        setSearchTerm: (searchTerm: string) => ({ searchTerm }),
        initiateNewItemInCategory: (category: string) => ({ category }),
        initiateNewItemInlineInCategory: (category: string) => ({ category }),
        cancelNewItem: true,
        saveNewItem: (itemName: string) => ({ itemName }),
        saveNewItemComplete: true,
        setLastFocusedItemIndex: (index: number) => ({ index }),
        setLastFocusedItemByKey: (key: string | number) => ({ key }), // A wrapper over setLastFocusedItemIndex
        focusNextItem: true,
        focusPreviousItem: true,
        toggleAccordion: (key: string) => ({ key }),
        toggleListItemAccordion: (key: string) => ({ key }),
        setZenMode: (zenMode: boolean, trigger: ZenModeTrigger) => ({ zenMode, trigger }),
        toggleZenMode: (trigger: ZenModeTrigger) => ({ trigger }),
    }),
    reducers({
        isSidebarShown: [
            true,
            {
                hideSidebar: () => false,
                showSidebar: () => true,
                toggleSidebar: (isSidebarShown) => !isSidebarShown,
            },
        ],
        sidebarWidth: [
            DEFAULT_SIDEBAR_WIDTH_PX,
            {
                setSidebarWidth: (_, { width }) => width,
            },
        ],
        sidebarOverslide: [
            // Overslide is how far beyond the min/max sidebar width the cursor has moved
            0,
            {
                setSidebarOverslide: (_, { overslide }) => overslide,
            },
        ],
        isResizeInProgress: [
            false,
            {
                beginResize: () => true,
                endResize: () => false,
            },
        ],
        isNavCollapsedDesktop: [
            false,
            { persist: true },
            {
                toggleNavCollapsed: (state, { override }) => override ?? !state,
            },
        ],
        isNavShownMobile: [
            false,
            { persist: true },
            {
                showNavOnMobile: () => true,
                hideNavOnMobile: () => false,
            },
        ],
        isSidebarKeyboardShortcutAcknowledged: [
            false,
            {
                persist: true,
            },
            {
                acknowledgeSidebarKeyboardShortcut: () => true,
            },
        ],
        activeNavbarItemIdRaw: [
            Scene.Dashboards as string,
            {
                persist: true,
            },
            {
                showSidebar: (state, { newNavbarItemId }) => newNavbarItemId || state,
                setScene: (state, { sceneId }) => sceneId || state,
            },
        ],
        isSearchShown: [
            true,
            {
                setIsSearchShown: (_, { isSearchShown }) => isSearchShown,
            },
        ],
        internalSearchTerm: [
            // Do not reference this outside of this file
            // `searchTerm` is the outwards-facing value, as it's made empty when search is hidden
            '',
            {
                setSearchTerm: (_, { searchTerm }) => searchTerm,
            },
        ],
        lastFocusedItemIndex: [
            -1 as number,
            {
                setLastFocusedItemIndex: (_, { index }) => index,
            },
        ],
        accordionCollapseMapping: [
            {} as Record<string, boolean>,
            {
                persist: true,
            },
            {
                toggleAccordion: (state, { key }) => ({
                    ...state,
                    [key]: !state[key],
                }),
            },
        ],
        listItemAccordionCollapseMapping: [
            {} as Record<string, boolean>,
            {
                persist: true,
            },
            {
                toggleListItemAccordion: (state, { key }) => ({
                    ...state,
                    [key]: !state[key],
                }),
            },
        ],
        newItemInlineCategory: [
            null as string | null,
            {
                initiateNewItemInlineInCategory: (_, { category }) => category,
                saveNewItemComplete: () => null,
                cancelNewItem: () => null,
                toggleSidebar: () => null,
                showSidebar: () => null,
                hideSidebar: () => null,
            },
        ],
        savingNewItem: [
            false,
            {
                saveNewItem: () => true,
                saveNewItemComplete: () => false,
            },
        ],
        zenMode: [
            false,
            { persist: true },
            {
                setZenMode: (_, { zenMode }) => zenMode,
            },
        ],
    }),
    listeners(({ actions, values }) => ({
        setZenMode: ({ zenMode, trigger }) => {
            posthog.capture('zen mode toggled', { enabled: zenMode, trigger })
        },
        toggleZenMode: ({ trigger }) => {
            actions.setZenMode(!values.zenMode, trigger)
        },
        initiateNewItemInCategory: ({ category: categoryKey }) => {
            let category = values.activeNavbarItem?.logic.values.contents?.find((item) => item.key === categoryKey)
            if (!category) {
                // Only the SQL editor uses this component, with the tree view, the activeNavbarItem doesnt get properly set, so lets fallback
                category = (
                    values.navbarItemIdMapping[Scene.SQLEditor] as SidebarNavbarItem
                )?.logic.values.contents?.find((item) => item.key === categoryKey)
                if (!category) {
                    throw new Error(`Sidebar category '${categoryKey}' doesn't exist`)
                }
            }

            if (!category.onAdd || typeof category.onAdd !== 'function') {
                throw new Error(`Sidebar category '${categoryKey}' doesn't support onAdd`)
            }

            if (category.onAdd.length === 0) {
                ;(category.onAdd as () => void)() // If a zero-arg function, call it immediately
            } else {
                actions.initiateNewItemInlineInCategory(categoryKey) // Otherwise initiate inline item creation
            }
        },
        saveNewItem: async ({ itemName }) => {
            try {
                const categoryKey = values.newItemInlineCategory
                if (!categoryKey) {
                    throw new Error(`Can't save new sidebar item without a category`)
                }
                const category = values.activeNavbarItem?.logic.values.contents?.find(
                    (item) => item.key === categoryKey
                )
                if (!category) {
                    throw new Error(`Sidebar category '${categoryKey}' doesn't exist`)
                } else if (!category.onAdd || typeof category.onAdd !== 'function') {
                    throw new Error(`Sidebar category '${categoryKey}' doesn't support onAdd`)
                }
                await category.onAdd(itemName)
            } catch (e) {
                posthog.captureException(e)
                console.error(e)
                lemonToast.error('Something went wrong while saving the item. Please try again.')
            } finally {
                actions.saveNewItemComplete()
            }
        },
        syncSidebarWidthWithMouseMove: ({ delta }) => {
            const newWidthRaw = values.sidebarWidth + values.sidebarOverslide + delta
            let newWidth = newWidthRaw
            if (newWidth < MINIMUM_SIDEBAR_WIDTH_PX) {
                newWidth = MINIMUM_SIDEBAR_WIDTH_PX
            } else if (newWidth > MAXIMUM_SIDEBAR_WIDTH_PX) {
                newWidth = MAXIMUM_SIDEBAR_WIDTH_PX
            }
            if (newWidth > window.innerWidth * (MAXIMUM_SIDEBAR_WIDTH_PERCENTAGE / 100)) {
                newWidth = window.innerWidth * (MAXIMUM_SIDEBAR_WIDTH_PERCENTAGE / 100)
            }
            actions.setSidebarWidth(newWidth)
            actions.setSidebarOverslide(newWidthRaw - newWidth)
            if (newWidthRaw < MINIMUM_SIDEBAR_WIDTH_PX / 2) {
                if (values.isSidebarShown) {
                    actions.hideSidebar()
                }
            } else {
                if (!values.isSidebarShown) {
                    actions.showSidebar()
                }
            }
        },
        syncSidebarWidthWithViewport: () => {
            if (values.sidebarWidth > window.innerWidth * (MAXIMUM_SIDEBAR_WIDTH_PERCENTAGE / 100)) {
                // Clamp
                actions.setSidebarWidth(window.innerWidth * (MAXIMUM_SIDEBAR_WIDTH_PERCENTAGE / 100))
            }
        },
        endResize: () => {
            actions.setSidebarOverslide(values.isSidebarShown ? 0 : -MINIMUM_SIDEBAR_WIDTH_PX)
        },
        toggleSidebar: () => {
            actions.endResize()
        },
        focusNextItem: () => {
            const nextIndex = values.lastFocusedItemIndex + 1
            if (nextIndex < values.sidebarContentsFlattened.length) {
                actions.setLastFocusedItemIndex(nextIndex)
            }
        },
        focusPreviousItem: () => {
            const nextIndex = values.lastFocusedItemIndex - 1
            if (nextIndex >= -1) {
                actions.setLastFocusedItemIndex(nextIndex)
            }
        },
        setLastFocusedItemByKey: ({ key }) => {
            const index = values.sidebarContentsFlattened.findIndex((item) =>
                Array.isArray(item.key) ? item.key.includes(key as string) : item.key === key
            )
            if (index !== -1) {
                actions.setLastFocusedItemIndex(index)
            }
        },
    })),
    selectors({
        zenModeFromUrl: [
            () => [router.selectors.searchParams],
            (searchParams: Record<string, any>): boolean => {
                // Enable zen mode via ?zen or ?zen=true or ?zen=1
                const zenParam = searchParams?.zen
                return zenParam !== undefined && zenParam !== 'false' && zenParam !== '0'
            },
        ],
        mode: [
            (s) => [
                s.sceneConfig,
                s.isCurrentOrganizationUnavailable,
                s.zenMode,
                s.activeSceneId,
                featureFlagLogic.selectors.featureFlags,
            ],
            (
                sceneConfig: null | import('scenes/sceneTypes').SceneConfig,
                isCurrentOrganizationUnavailable: boolean,
                zenMode: boolean,
                activeSceneId: string | null,
                featureFlags: import('lib/logic/featureFlagLogic').FeatureFlagsSet
            ): Navigation3000Mode => {
                if (zenMode) {
                    return 'zen'
                }
                if (isCurrentOrganizationUnavailable) {
                    return 'minimal'
                }
                if (sceneConfig?.layout === 'plain' && !sceneConfig.allowUnauthenticated) {
                    if (activeSceneId === Scene.Onboarding) {
                        // A flow variant can opt to own the whole viewport (no navbar); the
                        // existing ONBOARDING_NAVBAR='hide' flag stays as a separate override.
                        const variantHidesNavbar =
                            onboardingVariantChrome(resolveOnboardingFlowVariant(featureFlags)) === 'none'
                        if (variantHidesNavbar || featureFlags[FEATURE_FLAGS.ONBOARDING_NAVBAR] === 'hide') {
                            return 'none'
                        }
                    }
                    return 'minimal'
                }
                return sceneConfig?.layout !== 'plain' ? 'full' : 'none'
            },
        ],
        isNavShown: [
            (s) => [s.isNavShownMobile, s.mobileLayout],
            (isNavShownMobile: boolean, mobileLayout: boolean): boolean => !mobileLayout || isNavShownMobile,
        ],
        isNavCollapsed: [
            (s) => [s.isNavCollapsedDesktop, s.mobileLayout],
            (isNavCollapsedDesktop: boolean, mobileLayout: boolean): boolean => !mobileLayout && isNavCollapsedDesktop,
        ],
        navbarItems: [
            (s) => [
                featureFlagLogic.selectors.featureFlags,
                dashboardsModel.selectors.dashboardsLoading,
                dashboardsModel.selectors.pinnedDashboards,
                s.savedFilters,
                s.savedFiltersLoading,
            ],
            (
                featureFlags: import('lib/logic/featureFlagLogic').FeatureFlagsSet,
                dashboardsLoading: boolean,
                pinnedDashboards: (
                    | import('~/types').DashboardBasicType
                    | import('~/types').DashboardType<
                          import('~/types').QueryBasedInsightModel<
                              import('../../queries/schema').Node<Record<string, any>>
                          >
                      >
                )[],
                savedFilters: import('~/types').SavedSessionRecordingPlaylistsResult,
                savedFiltersLoading: boolean
            ): NavbarItem[][] => {
                return [
                    [
                        {
                            identifier: Scene.ProjectHomepage,
                            label: 'Home',
                            icon: <IconHome />,
                            to: urls.projectRoot(),
                        },
                        {
                            identifier: Scene.Dashboards,
                            label: 'Dashboards',
                            icon: <IconDashboard />,
                            tooltipDocLink: 'https://posthog.com/docs/product-analytics/dashboards',
                            to: urls.dashboards(),
                            sideAction:
                                pinnedDashboards.length > 0
                                    ? {
                                          identifier: 'pinned-dashboards-dropdown',
                                          dropdown: {
                                              overlay: (
                                                  <LemonMenuOverlay
                                                      items={[
                                                          {
                                                              title: 'Pinned dashboards',
                                                              items: pinnedDashboards.map((dashboard) => ({
                                                                  label: dashboard.name,
                                                                  to: urls.dashboard(dashboard.id),
                                                              })),
                                                              footer: dashboardsLoading && (
                                                                  <div className="px-2 py-1 text-tertiary">
                                                                      <Spinner /> Loading…
                                                                  </div>
                                                              ),
                                                          },
                                                      ]}
                                                  />
                                              ),
                                              placement: 'bottom-end',
                                          },
                                      }
                                    : undefined,
                        },
                        {
                            identifier: Scene.Notebooks,
                            label: 'Notebooks',
                            icon: <IconNotebook />,
                            to: urls.notebooks(),
                            tooltipDocLink: 'https://posthog.com/docs/notebooks',
                        },
                        {
                            identifier: Scene.DataManagement,
                            label: 'Data management',
                            icon: <IconDatabase />,
                            to: urls.eventDefinitions(),
                            tooltipDocLink: 'https://posthog.com/docs/data',
                        },
                        {
                            identifier: Scene.Persons,
                            label: 'People and groups',
                            icon: <IconPeople />,
                            to: urls.persons(),
                            tooltipDocLink: 'https://posthog.com/docs/data/persons',
                        },
                        {
                            identifier: Scene.ExploreEvents,
                            label: 'Activity',
                            icon: <IconLive />,
                            to: urls.activity(),
                            tooltipDocLink: 'https://posthog.com/docs/data/events',
                        },
                    ],
                    [
                        {
                            identifier: Scene.SavedInsights,
                            label: 'Product analytics',
                            icon: <IconGraph />,
                            tooltipDocLink: 'https://posthog.com/docs/product-analytics/insights',
                            to: urls.savedInsights(),
                            sideAction:
                                getAppContext()?.resource_access_control?.[AccessControlResourceType.Insight] ===
                                AccessControlLevel.Editor
                                    ? {
                                          icon: <IconPlusSmall />, // The regular plus is too big
                                          to: urls.insightNew(),
                                          tooltip: 'New insight',
                                          identifier: Scene.Insight,
                                      }
                                    : undefined,
                        },
                        {
                            identifier: Scene.WebAnalytics,
                            label: 'Web analytics',
                            icon: <IconPieChart />,
                            to: urls.webAnalytics(),
                            tooltipDocLink: 'https://posthog.com/docs/web-analytics/getting-started',
                        },
                        featureFlags[FEATURE_FLAGS.REVENUE_ANALYTICS]
                            ? {
                                  identifier: Scene.RevenueAnalytics,
                                  label: 'Revenue analytics',
                                  icon: <IconPiggyBank />,
                                  to: urls.revenueAnalytics(),
                                  tag: 'alpha' as const,
                                  tooltipDocLink: 'https://posthog.com/docs/revenue-analytics/getting-started',
                              }
                            : null,
                        {
                            identifier: Scene.Replay,
                            label: 'Session replay',
                            icon: <IconRewindPlay />,
                            to: urls.replay(),
                            tooltipDocLink: 'https://posthog.com/docs/session-replay',
                            sideAction: {
                                identifier: 'replay-dropdown',
                                dropdown: {
                                    overlay: (
                                        <LemonMenuOverlay
                                            items={
                                                savedFilters.count > 0
                                                    ? [
                                                          {
                                                              title: 'Saved filters',
                                                              items: savedFilters.results.map((savedFilter) => ({
                                                                  label:
                                                                      savedFilter.name ||
                                                                      savedFilter.derived_name ||
                                                                      'Unnamed',
                                                                  to: combineUrl(urls.replay(ReplayTabs.Home), {
                                                                      savedFilterId: savedFilter.short_id,
                                                                  }).url,
                                                              })),
                                                              footer: savedFiltersLoading && (
                                                                  <div className="px-2 py-1 text-tertiary">
                                                                      <Spinner /> Loading…
                                                                  </div>
                                                              ),
                                                          },
                                                      ]
                                                    : [
                                                          {
                                                              label: 'All recordings',
                                                              to: urls.replay(ReplayTabs.Home),
                                                          },
                                                          {
                                                              label: 'Saved filters',
                                                              to: urls.replay(ReplayTabs.Home),
                                                          },
                                                      ]
                                            }
                                        />
                                    ),
                                    placement: 'bottom-end',
                                },
                            },
                        },
                        {
                            identifier: Scene.FeatureFlags,
                            label: 'Feature flags',
                            icon: <IconToggle />,
                            to: urls.featureFlags(),
                            tooltipDocLink: 'https://posthog.com/docs/feature-flags/creating-feature-flags',
                        },
                        {
                            identifier: Scene.Experiments,
                            label: 'Experiments',
                            icon: <IconTestTube />,
                            to: urls.experiments(),
                            tooltipDocLink: 'https://posthog.com/docs/experiments/creating-an-experiment',
                        },
                        {
                            identifier: Scene.Surveys,
                            label: 'Surveys',
                            icon: <IconMessage />,
                            to: urls.surveys(),
                            tooltipDocLink: 'https://posthog.com/docs/surveys/creating-surveys',
                        },
                        featureFlags[FEATURE_FLAGS.PRODUCT_TOURS]
                            ? {
                                  identifier: Scene.ProductTours,
                                  label: 'Product tours',
                                  icon: <IconSpotlight />,
                                  tag: 'alpha' as const,
                                  to: urls.productTours(),
                              }
                            : null,
                        {
                            identifier: Scene.EarlyAccessFeatures,
                            label: 'Early access features',
                            icon: <IconRocket />,
                            to: urls.earlyAccessFeatures(),
                            tooltipDocLink: 'https://posthog.com/docs/feature-flags/early-access-feature-management',
                        },
                        featureFlags[FEATURE_FLAGS.USER_INTERVIEWS]
                            ? {
                                  identifier: Scene.UserInterviews,
                                  label: 'User research',
                                  icon: <IconChat />,
                                  tag: 'alpha' as const,
                                  to: urls.userInterviews(),
                              }
                            : null,
                        {
                            identifier: 'AIObservability',
                            label: 'AI observability',
                            icon: <IconLlmAnalytics />,
                            to: urls.aiObservabilityDashboard(),
                            tooltipDocLink: 'https://posthog.com/docs/ai-observability/dashboard',
                        },
                        featureFlags[FEATURE_FLAGS.MCP_ANALYTICS]
                            ? {
                                  identifier: 'MCPAnalytics',
                                  label: 'MCP analytics',
                                  icon: <IconLlmAnalytics />,
                                  to: urls.mcpAnalyticsDashboard(),
                                  tooltipDocLink: 'https://posthog.com/docs/mcp-analytics/installation',
                              }
                            : null,
                        {
                            identifier: Scene.Logs,
                            label: 'Logs',
                            icon: <IconLive />,
                            to: urls.logs(),
                            tag: 'beta' as const,
                            tooltipDocLink: 'https://posthog.com/docs/logs',
                        },
                        {
                            identifier: Scene.ErrorTracking,
                            label: 'Error tracking',
                            icon: <IconWarning />,
                            to: urls.errorTracking(),
                            tooltipDocLink: 'https://posthog.com/docs/error-tracking/stack-traces',
                        },
                        {
                            identifier: Scene.SQLEditor,
                            label: 'SQL editor',
                            icon: <IconServer />,
                            to: urls.sqlEditor(),
                            logic: editorSceneLogic,
                            tooltipDocLink: 'https://posthog.com/docs/data-warehouse/query#querying-sources-with-sql',
                        },
                        {
                            identifier: Scene.WebScripts,
                            label: 'Web scripts',
                            icon: <IconPlug />,
                            to: urls.webScripts(),
                            tooltipDocLink: 'https://posthog.com/docs/cdp/apps',
                        },
                        {
                            identifier: Scene.Heatmaps,
                            label: 'Heatmaps',
                            icon: <IconCursorClick />,
                            to: urls.heatmaps(),
                            tag: 'beta' as const,
                            tooltipDocLink: 'https://posthog.com/docs/toolbar/heatmaps',
                        },
                        featureFlags[FEATURE_FLAGS.LINKS]
                            ? {
                                  identifier: Scene.Links,
                                  label: 'Links',
                                  icon: <IconCursorClick />,
                                  to: urls.links(),
                                  tag: 'alpha' as const,
                                  tooltipDocLink: 'https://posthog.com/docs/links',
                              }
                            : null,
                        {
                            identifier: Scene.Workflows,
                            label: 'Workflows',
                            icon: <IconDecisionTree />,
                            to: urls.workflows(),
                        },
                    ].filter(isNotNil) as NavbarItem[],
                ]
            },
        ],
        navbarItemIdMapping: [
            (s) => [s.navbarItems],
            (navbarItems: NavbarItem[][]): Record<string, NavbarItem> => {
                return Object.fromEntries(navbarItems.flat().map((item) => [item.identifier, item]))
            },
        ],
        sidebarOverslideDirection: [
            (s) => [s.sidebarOverslide],
            (sidebarOverslide: number): 'min' | 'max' | null => {
                if (sidebarOverslide < 0) {
                    return 'min'
                } else if (sidebarOverslide > 0) {
                    return 'max'
                }
                return null
            },
        ],
        activeNavbarItem: [
            (s) => [s.activeNavbarItemId, s.navbarItemIdMapping],
            (
                activeNavbarItemId: string | null,
                navbarItemIdMapping: Record<string, NavbarItem>
            ): SidebarNavbarItem | null => {
                const item = activeNavbarItemId ? navbarItemIdMapping[activeNavbarItemId] : null
                return item && 'logic' in item ? (item as SidebarNavbarItem) : null
            },
        ],
        searchTerm: [
            (s) => [s.internalSearchTerm, s.isSearchShown],
            (internalSearchTerm: string, isSearchShown: boolean): string => {
                return isSearchShown ? internalSearchTerm : ''
            },
        ],
        sidebarContentsFlattened: [
            (s) => [(state) => s.activeNavbarItem(state)?.logic?.findMounted()?.selectors.contents(state) || null],
            (sidebarContents: import('./types').SidebarCategory[] | null): BasicListItem[] | ExtendedListItem[] => {
                const flattenItems = (items: any[]): (BasicListItem | ExtendedListItem)[] => {
                    return items.flatMap((item) => {
                        if ('items' in item) {
                            return flattenItems(item.items)
                        }
                        return item
                    })
                }
                return sidebarContents ? flattenItems(sidebarContents) : []
            },
        ],
        normalizedActiveListItemKey: [
            (s) => [
                (state) =>
                    s.activeNavbarItem(state)?.logic?.findMounted()?.selectors.activeListItemKey?.(state) || null,
            ],
            (
                activeListItemKey: number | string | [string, number | string] | null
            ): string | number | string[] | null =>
                activeListItemKey
                    ? Array.isArray(activeListItemKey)
                        ? activeListItemKey.join(ITEM_KEY_PART_SEPARATOR)
                        : activeListItemKey
                    : null,
        ],
        isListItemVisible: [
            (s) => [s.listItemAccordionCollapseMapping],
            (listItemAccordionCollapseMapping: Record<string, boolean>) => {
                return (key: string): boolean => {
                    // Split the key into parts to check each parent's visibility
                    const parts = key.split(ITEM_KEY_PART_SEPARATOR)
                    // Check if any parent is collapsed
                    for (let i = 1; i < parts.length; i++) {
                        const parentKey = parts.slice(0, i).join(ITEM_KEY_PART_SEPARATOR)
                        if (listItemAccordionCollapseMapping[parentKey]) {
                            return false
                        }
                    }
                    return true
                }
            },
        ],
        activeNavbarItemId: [
            (s) => [s.activeNavbarItemIdRaw],
            (activeNavbarItemIdRaw: string): string | null => {
                if (activeNavbarItemIdRaw === Scene.SQLEditor) {
                    return Scene.SQLEditor
                }
                return null
            },
        ],
        newItemCategory: [
            (s) => [
                (state) => s.activeNavbarItem(state)?.logic?.findMounted()?.selectors.contents(state) || null,
                s.newItemInlineCategory,
                router.selectors.location,
            ],
            (
                sidebarContents: import('./types').SidebarCategory[] | null,
                newItemInlineCategory: string | null,
                location: {
                    hash: string
                    pathname: string
                    search: string
                }
            ): string | null => {
                if (!sidebarContents) {
                    return null
                }
                if (newItemInlineCategory) {
                    return newItemInlineCategory
                }
                return (
                    sidebarContents.find(
                        (category) => typeof category.onAdd === 'string' && category.onAdd === location.pathname
                    )?.key || null
                )
            },
        ],
    }),
    subscriptions(({ props, cache, actions, values }) => ({
        isResizeInProgress: (isResizeInProgress) => {
            if (isResizeInProgress) {
                cache.onMouseMove = (e: MouseEvent): void => actions.syncSidebarWidthWithMouseMove(e.movementX)
                cache.onMouseUp = (e: MouseEvent): void => {
                    if (e.button === 0) {
                        actions.endResize()
                    }
                }
                document.addEventListener('mousemove', cache.onMouseMove)
                document.addEventListener('mouseup', cache.onMouseUp)
                return () => {}
            }
            document.removeEventListener('mousemove', cache.onMouseMove)
            document.removeEventListener('mouseup', cache.onMouseUp)
        },
        sidebarContentsFlattened: (sidebarContentsFlattened) => {
            for (const item of sidebarContentsFlattened) {
                if (!item.ref) {
                    item.ref = React.createRef() // Inject refs for keyboard navigation
                }
            }
            actions.setLastFocusedItemIndex(-1) // Reset focused item index on contents change
        },
        lastFocusedItemIndex: (lastFocusedItemIndex) => {
            if (lastFocusedItemIndex >= 0) {
                const item = values.sidebarContentsFlattened[lastFocusedItemIndex]
                item.ref?.current?.focus()
            } else {
                props.inputElement?.focus()
            }
        },
        zenModeFromUrl: (zenModeFromUrl: boolean) => {
            // Enable zen mode when URL parameter is present (e.g., ?zen or ?zen=true)
            // Only enable, never disable from URL - user can manually disable
            if (zenModeFromUrl && !values.zenMode) {
                actions.setZenMode(true, 'url')
            }
        },
    })),
    events(({ props, actions, cache }) => ({
        afterMount: () => {
            cache.onResize = () => actions.syncSidebarWidthWithViewport()
            cache.onKeyDown = (e: KeyboardEvent) => {
                if (e.key === 'b' && (e.metaKey || e.ctrlKey)) {
                    actions.toggleSidebar()
                    e.preventDefault()
                }
                if (e.key === 'f' && e.shiftKey && (e.metaKey || e.ctrlKey)) {
                    actions.setIsSearchShown(true)
                    props.inputElement?.focus()
                    e.preventDefault()
                }
            }
            cache.disposables.add(() => {
                window.addEventListener('resize', cache.onResize)
                return () => window.removeEventListener('resize', cache.onResize)
            }, 'resizeListener')
            cache.disposables.add(() => {
                window.addEventListener('keydown', cache.onKeyDown)
                return () => window.removeEventListener('keydown', cache.onKeyDown)
            }, 'keydownListener')
        },
    })),
])
